import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Sparkles as SparklesIcon, TrendingUp, Plus, Check, Award, Trash2, Star, X, Bell, PenLine, Calendar, ChevronDown, ChevronUp, Lightbulb, Clock, ArrowUp, ArrowDown, Send } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getBereketActivities, getCustomActivities, getTodayBereket, logBereketActivity, addCustomActivity, deleteCustomActivity as deleteCustomActivityAPI, createPlannerEvent, getLocalDateString } from '../lib/api';
import ReminderSettings from '../components/ReminderSettings';

const getTodayDateKey = () => getLocalDateString();

const ACTIVITY_ICONS = [
  { id: 'star', icon: '⭐', label: 'Star' },
  { id: 'heart', icon: '❤️', label: 'Heart' },
  { id: 'pray', icon: '🤲', label: 'Dua' },
  { id: 'book', icon: '📖', label: 'Book' },
  { id: 'moon', icon: '🌙', label: 'Moon' },
  { id: 'sun', icon: '☀️', label: 'Sun' },
  { id: 'plant', icon: '🌱', label: 'Plant' },
  { id: 'gift', icon: '🎁', label: 'Gift' },
  { id: 'handshake', icon: '🤝', label: 'Help' },
  { id: 'family', icon: '👨‍👩‍👧', label: 'Family' },
];

// 21 načina za postizanje bereketa
const BEREKET_WAYS = [
  {
    number: 1,
    title_hr: 'Traži Allahov savjet',
    title_en: 'Seek Allah\'s Advice',
    description_hr: 'Ako danas moraš donijeti odluku, traži bereket u njezinom ishodu savjetujući se s drugima i, što je najvažnije, tražeći Allahov savjet kroz istihara-namaz.',
    description_en: 'If you need to make a decision today, seek barakah in its outcome by consulting others and, most importantly, seeking Allah\'s advice through Istikhara prayer.',
    icon: '🤲',
    category: 'prayer'
  },
  {
    number: 2,
    title_hr: 'Svjesno izgovori Bismillah',
    title_en: 'Say Bismillah Consciously',
    description_hr: 'Za svaku aktivnost danas reci Bismillah (U ime Allaha), sa značenjem i razumijevanjem. Dopusti da ti se utisne svijest da tražiš bereket spominjanjem Njegovog imena.',
    description_en: 'For every activity today, say Bismillah (In the name of Allah) with meaning and understanding. Let the awareness sink in that you are seeking barakah by mentioning His name.',
    icon: '✨',
    category: 'dhikr'
  },
  {
    number: 3,
    title_hr: 'Udvostruči vrijeme s Kur\'anom',
    title_en: 'Double Your Quran Time',
    description_hr: 'Ako imaš naviku svakodnevno čitati određenu količinu Kur\'ana, pokušaj to udvostručiti i primijeti učinak bereketa koji ćeš osjetiti tijekom dana.',
    description_en: 'If you have a habit of reading a certain amount of Quran daily, try to double it and notice the effect of barakah you will feel throughout the day.',
    icon: '📖',
    category: 'quran'
  },
  {
    number: 4,
    title_hr: 'Udijeli sadaku nekome koga poznaješ',
    title_en: 'Give Charity to Someone You Know',
    description_hr: 'Pronađi nekoga za koga znaš da je u potrebi i pokušaj preuzeti jednu njegovu financijsku brigu bez da on to zna (npr. plati račun za struju, školarinu, stanarinu itd.).',
    description_en: 'Find someone you know who is in need and try to take over one of their financial burdens without them knowing (e.g., pay their electricity bill, tuition, rent, etc.).',
    icon: '💝',
    category: 'charity'
  },
  {
    number: 5,
    title_hr: 'Žrtvuj nešto radi Allaha',
    title_en: 'Sacrifice Something for Allah',
    description_hr: 'Odluči se na malu žrtvu tako što ćeš se nečega iskreno odreći radi Njega. Na primjer, isključi se s telefona u određenim satima kako bi se posvetio povezivanju s Allahom.',
    description_en: 'Make a small sacrifice by sincerely giving up something for His sake. For example, disconnect from your phone during certain hours to focus on connecting with Allah.',
    icon: '🎁',
    category: 'personal'
  },
  {
    number: 6,
    title_hr: 'Pretvori brige u dove',
    title_en: 'Turn Worries into Duas',
    description_hr: 'Pretvori svoje brige ili težnje u konkretne dove (molitve) koje redovito upućuješ Allahu u svim svojim namazima.',
    description_en: 'Turn your worries or aspirations into specific duas (prayers) that you regularly make to Allah in all your prayers.',
    icon: '🤲',
    category: 'prayer'
  },
  {
    number: 7,
    title_hr: 'Donosi salavate na Poslanika ﷺ',
    title_en: 'Send Salawat upon the Prophet ﷺ',
    description_hr: 'Postavi tajmer na 10–30 minuta i uči salavate na Poslanika (one koje učiš u namazu) u obilju. Razmisli o značenju svog prisjećanja na Poslanika.',
    description_en: 'Set a timer for 10-30 minutes and recite salawat upon the Prophet (the ones you recite in prayer) abundantly. Reflect on the meaning of your remembrance of the Prophet.',
    icon: '🌙',
    category: 'dhikr'
  },
  {
    number: 8,
    title_hr: 'Pobijedi frustracije zikrom',
    title_en: 'Overcome Frustrations with Dhikr',
    description_hr: 'Kad god osjetiš frustraciju, sjeti se Allaha; reci: SubhanAllah, Alhamdulillah, La ilahe illallah, Allahu Akbar. Ponavljaj to dok se frustracija ne smiri.',
    description_en: 'Whenever you feel frustrated, remember Allah; say: SubhanAllah, Alhamdulillah, La ilaha illallah, Allahu Akbar. Repeat until the frustration subsides.',
    icon: '💫',
    category: 'dhikr'
  },
  {
    number: 9,
    title_hr: 'Postavi nijjet za svoje obveze',
    title_en: 'Set Intention for Your Obligations',
    description_hr: 'Zapitaj se: „Koja je moja namjera iza mojih zadataka? Što želim postići? I kako ih najbolje mogu uskladiti s onim što Allah želi od mene?"',
    description_en: 'Ask yourself: "What is my intention behind my tasks? What do I want to achieve? And how can I best align them with what Allah wants from me?"',
    icon: '🎯',
    category: 'personal'
  },
  {
    number: 10,
    title_hr: 'Napiši zahvalnicu',
    title_en: 'Write a Thank You Note',
    description_hr: 'Napiši nekome iz svog posla, obitelji ili zajednice detaljnu poruku zahvale i spomeni sve dobro što čini i koliko ga cijeniš.',
    description_en: 'Write a detailed thank you message to someone from your work, family, or community, mentioning all the good they do and how much you appreciate them.',
    icon: '💌',
    category: 'charity'
  },
  {
    number: 11,
    title_hr: 'Odspavaj kratko (kajlulu)',
    title_en: 'Take a Short Nap (Qaylulah)',
    description_hr: 'Postavi namjeru da slijediš poslanikov sunnet i odspavaj kratko oko podnevnog namaza (oko 20 minuta). Pronađi park, klupu ili jednostavno zatvori oči u autu ili uredu.',
    description_en: 'Set the intention to follow the Prophet\'s sunnah and take a short nap around Dhuhr prayer (about 20 minutes). Find a park, bench, or simply close your eyes in your car or office.',
    icon: '😴',
    category: 'sunnah'
  },
  {
    number: 12,
    title_hr: 'Odazovi se Allahu – na vrijeme',
    title_en: 'Answer Allah\'s Call – On Time',
    description_hr: 'Obveži se da nećeš kasniti na namaz, već da ćeš dolaziti među prvima. Neka ti geslo bude ono što je Musa rekao: „…i požurio sam k Tebi, Gospodaru moj, da budeš zadovoljan." (Kur\'an 20:84)',
    description_en: 'Commit to not being late for prayer, but rather being among the first. Let your motto be what Musa said: "...and I hastened to You, my Lord, that You be pleased." (Quran 20:84)',
    icon: '⏰',
    category: 'prayer'
  },
  {
    number: 13,
    title_hr: 'Uzmi pauzu za duha-namaz',
    title_en: 'Take a Break for Duha Prayer',
    description_hr: 'Uživaj u prijepodnevnoj pauzi uz namaz duha, kao što je bila praksa Poslanika Muhammeda ﷺ. Možeš klanjati 2, 4 ili do 8 rekata.',
    description_en: 'Enjoy a mid-morning break with Duha prayer, as was the practice of Prophet Muhammad ﷺ. You can pray 2, 4, or up to 8 rakats.',
    icon: '☀️',
    category: 'prayer'
  },
  {
    number: 14,
    title_hr: 'Pripremi svoju oporuku',
    title_en: 'Prepare Your Will',
    description_hr: 'To te podsjeća na smrt i usmjerava tvoj pogled prema ahiretu. Pomaže ti da se odvojiš od dunjaluka i usredotočiš na ono što je zaista važno.',
    description_en: 'This reminds you of death and directs your gaze towards the Hereafter. It helps you detach from this world and focus on what truly matters.',
    icon: '📜',
    category: 'personal'
  },
  {
    number: 15,
    title_hr: 'Posadi ili njeguj drvo/biljku',
    title_en: 'Plant or Nurture a Tree/Plant',
    description_hr: 'Briga o živom biću izvor je bereketa jer te stalno podsjeća na Allaha. Nabavi malu biljku za ured ili zasadi nešto u vrtu i brini se o njezinim potrebama.',
    description_en: 'Caring for a living being is a source of barakah as it constantly reminds you of Allah. Get a small plant for your office or plant something in your garden and take care of its needs.',
    icon: '🌱',
    category: 'sunnah'
  },
  {
    number: 16,
    title_hr: 'Ne rasipaj',
    title_en: 'Don\'t Waste',
    description_hr: 'Razmisli o nečemu što imaš, a osjećaš da rasipaš (npr. vodu, hranu, struju itd.). Odaberi jedno i postavi cilj da to više ne rasipaš.',
    description_en: 'Think about something you have that you feel you\'re wasting (e.g., water, food, electricity, etc.). Choose one and set a goal to no longer waste it.',
    icon: '💧',
    category: 'personal'
  },
  {
    number: 17,
    title_hr: 'Ustani rano',
    title_en: 'Wake Up Early',
    description_hr: 'Vrijeme od zore do izlaska sunca prilika je za istinski doživljaj bereketa. Uz jutarnje duhovne aktivnosti, pokušaj napredovati i u važnim, ali nehitnim ciljevima.',
    description_en: 'The time from dawn to sunrise is an opportunity to truly experience barakah. Along with morning spiritual activities, try to make progress on important but non-urgent goals.',
    icon: '🌅',
    category: 'sunnah'
  },
  {
    number: 18,
    title_hr: 'Budi svjestan svetih trenutaka',
    title_en: 'Be Aware of Sacred Moments',
    description_hr: 'Budi duhovno osjetljiv na posebna vremena, poput trenutaka prije iftara, prije sabah-namaza, kada pada kiša i posljednjeg sata petkom. Ne dopusti da prođu bez dove.',
    description_en: 'Be spiritually sensitive to special times, such as moments before iftar, before Fajr prayer, when it rains, and the last hour on Friday. Don\'t let them pass without dua.',
    icon: '🕐',
    category: 'prayer'
  },
  {
    number: 19,
    title_hr: 'Ne raspravljaj se',
    title_en: 'Don\'t Argue',
    description_hr: 'Odustani od rasprava čak i ako si u pravu. Saslušaj drugu osobu i, ako je spremna čuti te, podijeli svoje mišljenje s poštovanjem bez očekivanja da se mora složiti.',
    description_en: 'Give up arguments even if you\'re right. Listen to the other person and, if they\'re willing to hear you, share your opinion respectfully without expecting them to agree.',
    icon: '🤝',
    category: 'personal'
  },
  {
    number: 20,
    title_hr: 'Provodi kvalitetno vrijeme s roditeljima',
    title_en: 'Spend Quality Time with Parents',
    description_hr: 'Provedi vrijeme s roditeljima izvan rutine. Ako su živi, razgovaraj s njima iskreno i dublje. Ako su preselili, uputi dovu za njih.',
    description_en: 'Spend time with your parents outside of routine. If they\'re alive, talk to them sincerely and deeply. If they have passed, make dua for them.',
    icon: '👨‍👩‍👧',
    category: 'family'
  },
  {
    number: 21,
    title_hr: 'Redovito koristi misvak',
    title_en: 'Use Miswak Regularly',
    description_hr: 'Korištenje misvaka podsjeća te na poslanikov sunnet i praksu koja donosi Allahovo zadovoljstvo. Pomaže ti razviti svijest o Bogu čak i u oralnoj higijeni.',
    description_en: 'Using miswak reminds you of the Prophet\'s sunnah and a practice that brings Allah\'s pleasure. It helps you develop God-consciousness even in oral hygiene.',
    icon: '🪥',
    category: 'sunnah'
  }
];

export default function Bereket({ user }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [customActivities, setCustomActivities] = useState([]);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isSecret, setIsSecret] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showBereketWays, setShowBereketWays] = useState(false);
  const [expandedWay, setExpandedWay] = useState(null);
  const [newActivity, setNewActivity] = useState({
    name_en: '',
    name_hr: '',
    description_en: '',
    description_hr: '',
    base_points: 1,
    max_daily_points: null,
    icon: 'star'
  });

  // Daily Plan state (items + completions stored separately)
  const [dailyPlan, setDailyPlan] = useState(() => {
    try {
      const saved = localStorage.getItem('bereket_daily_plan');
      const data = saved ? JSON.parse(saved) : { date: '', items: [] };
      // Reset if different day
      if (data.date !== getTodayDateKey()) {
        return { date: getTodayDateKey(), items: [] };
      }
      return data;
    } catch { return { date: getTodayDateKey(), items: [] }; }
  });
  // Track completed plan items locally (for custom activities that can't log to bereket_logs)
  const [planCompletions, setPlanCompletions] = useState(() => {
    try {
      const saved = localStorage.getItem('bereket_plan_completions');
      const data = saved ? JSON.parse(saved) : { date: '', completed: [] };
      if (data.date !== getTodayDateKey()) {
        return { date: getTodayDateKey(), completed: [] };
      }
      return data;
    } catch { return { date: getTodayDateKey(), completed: [] }; }
  });
  const [showPlannerModal, setShowPlannerModal] = useState(null); // activity to add to planner
  const [plannerDate, setPlannerDate] = useState(getTodayDateKey());
  const [plannerTime, setPlannerTime] = useState('09:00');
  const [plannerReminder, setPlannerReminder] = useState(false);
  const [addedAnimation, setAddedAnimation] = useState(null); // activity_id for animation

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activitiesData, customData, todayData] = await Promise.all([
        getBereketActivities(),
        getCustomActivities(),
        getTodayBereket(),
      ]);
      setActivities(activitiesData);
      setCustomActivities(customData || []);
      setTodayData(todayData);
    } catch (error) {
      console.error('Error fetching bereket data:', error);
    }
  };

  const logActivity = async (activity) => {
    setLoading(true);
    try {
      const activityName = lang === 'hr' ? activity.name_hr : activity.name_en;
      await logBereketActivity(activity.activity_id, activity.base_points, isSecret, activityName);
      setIsSecret(false);
      setSelectedActivity(null);
      toast.success(lang === 'hr' ? `+${activity.base_points} bodova!` : `+${activity.base_points} points!`);
      fetchData();
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error(error.message || 'Failed to log activity');
    } finally {
      setLoading(false);
    }
  };

  const addCustomActivityHandler = async () => {
    if (!newActivity.name_en || !newActivity.name_hr) {
      toast.error(lang === 'hr' ? 'Unesite naziv aktivnosti' : 'Enter activity name');
      return;
    }

    try {
      await addCustomActivity(newActivity);
      toast.success(lang === 'hr' ? 'Aktivnost dodana!' : 'Activity added!');
      setShowAddModal(false);
      setNewActivity({
        name_en: '',
        name_hr: '',
        description_en: '',
        description_hr: '',
        base_points: 1,
        max_daily_points: null,
        icon: 'star'
      });
      fetchData();
    } catch (error) {
      console.error('Error adding custom activity:', error);
      toast.error(lang === 'hr' ? 'Greška pri dodavanju' : 'Error adding activity');
    }
  };

  const deleteCustomActivity = async (activityId) => {
    if (!confirm(lang === 'hr' ? 'Obriši aktivnost?' : 'Delete activity?')) return;

    try {
      await deleteCustomActivityAPI(activityId);
      toast.success(lang === 'hr' ? 'Aktivnost obrisana!' : 'Activity deleted!');
      fetchData();
    } catch (error) {
      console.error('Error deleting activity:', error);
    }
  };

  // ===== DAILY PLAN FUNCTIONS =====
  const saveDailyPlan = (items) => {
    const plan = { date: getTodayDateKey(), items };
    setDailyPlan(plan);
    localStorage.setItem('bereket_daily_plan', JSON.stringify(plan));
  };

  const addToDailyPlan = (activity) => {
    if (dailyPlan.items.find(i => i.activity_id === activity.activity_id)) {
      toast.info(lang === 'hr' ? 'Već je u planu' : 'Already in plan');
      return;
    }
    const item = {
      activity_id: activity.activity_id,
      name_hr: activity.name_hr,
      name_en: activity.name_en,
      base_points: activity.base_points,
      completed: false,
    };
    saveDailyPlan([...dailyPlan.items, item]);
    // Trigger animation
    setAddedAnimation(activity.activity_id);
    setTimeout(() => setAddedAnimation(null), 800);
    toast.success(lang === 'hr' ? 'Dodano u dnevni plan!' : 'Added to daily plan!');
  };

  const removeFromDailyPlan = (activityId) => {
    saveDailyPlan(dailyPlan.items.filter(i => i.activity_id !== activityId));
  };

  const movePlanItem = (index, direction) => {
    const items = [...dailyPlan.items];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    saveDailyPlan(items);
  };

  const addActivityToPlanner = async () => {
    if (!showPlannerModal) return;
    try {
      const activityName = lang === 'hr' ? showPlannerModal.name_hr : showPlannerModal.name_en;
      const startTime = `${plannerDate}T${plannerTime}:00`;
      const [h, m] = plannerTime.split(':').map(Number);
      const endH = ((h + 1) % 24).toString().padStart(2, '0');
      const endTime = `${plannerDate}T${endH}:${m.toString().padStart(2, '0')}:00`;
      
      const eventData = {
        title: activityName,
        event_type: 'bereket',
        start_time: startTime,
        end_time: endTime,
        description: `Bereket: ${showPlannerModal.base_points} bodova`,
      };

      // Only include reminder_minutes if reminder is enabled
      if (plannerReminder) {
        eventData.reminder_minutes = 15;
      }

      // Only include bereket_activity_id if it's a standard bereket activity (not custom)
      // Custom activity IDs don't exist in bereket_activities table (FK constraint)
      const actId = showPlannerModal.activity_id || showPlannerModal.id;
      const isStandardActivity = activities.some(a => a.activity_id === actId || a.id === actId);
      if (isStandardActivity && actId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(actId)) {
        eventData.bereket_activity_id = actId;
      }
      
      await createPlannerEvent(eventData);
      
      toast.success(lang === 'hr' ? 'Dodano u planer!' : 'Added to planner!');
      setShowPlannerModal(null);
    } catch (error) {
      console.error('Error adding to planner:', error);
      toast.error(lang === 'hr' ? 'Greška pri dodavanju' : 'Error adding');
    }
  };

  const isActivityLogged = (activityId) => {
    // Check bereket_logs first (standard activities)
    if (todayData?.activities_logged?.[activityId] > 0) return true;
    // Check local plan completions (for custom activities)
    return planCompletions.completed.includes(activityId);
  };

  const getActivityPoints = (activityId) => {
    return todayData?.activities_logged?.[activityId] || 0;
  };

  // Aktivnosti koje se automatski prate u drugim modulima (Vaktije/PrayerTimes i Quran)
  // Makni namaze jer se označavaju u modulu Vaktije
  // Makni duplikate za džuz - ostavi samo jedan
  const excludedActivitiesEN = [
    'Pray Fajr',
    'Pray Dhuhr',
    'Pray Asr',
    'Pray Maghrib',
    'Pray Isha',
    'Read Quran (1 page)',
    'Listen to Quran (1 surah)'
  ];
  
  // Hrvatski nazivi za namaze
  const excludedActivitiesHR = [
    'Sabah namaz',
    'Podne namaz',
    'Ikindija namaz',
    'Akšam namaz',
    'Jacija namaz',
    'Sabah',
    'Podne',
    'Ikindija',
    'Akšam',
    'Jacija'
  ];

  // Filtriranje aktivnosti - ukloni namaze i duplikate džuza
  const filterActivities = (activityList, category) => {
    const filtered = activityList.filter((a) => {
      if (a.category !== category) return false;
      
      // Provjeri engleski naziv
      if (excludedActivitiesEN.includes(a.name_en)) return false;
      
      // Provjeri hrvatski naziv
      if (excludedActivitiesHR.includes(a.name_hr)) return false;
      
      // Provjeri sadrži li naziv "namaz" (za sve varijante)
      const nameEN = (a.name_en || '').toLowerCase();
      const nameHR = (a.name_hr || '').toLowerCase();
      if (nameEN.includes('pray ') || nameHR.includes('namaz')) return false;
      
      return true;
    });
    
    // Ukloni duplikate džuza - zadrži samo prvu instancu
    const seenJuz = new Set();
    return filtered.filter((a) => {
      const isJuzActivity = a.name_en?.toLowerCase().includes('juz') || 
                           a.name_hr?.toLowerCase().includes('džuz');
      if (isJuzActivity) {
        if (seenJuz.has('juz')) return false;
        seenJuz.add('juz');
      }
      return true;
    });
  };

  const basicActivities = filterActivities(activities, 'basic');
  const mediumActivities = filterActivities(activities, 'medium');
  const advancedActivities = filterActivities(activities, 'advanced');

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-2">
              {lang === 'hr' ? 'Bereket Bodovi' : 'Barakah Points'}
            </h1>
            <p className="text-[#57534E] font-body">
              {lang === 'hr'
                ? '21 jednostavan način za postizanje bereketa'
                : '21 Simple Ways to Increase Barakah'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowReminderModal(true)}
              data-testid="bereket-reminder-btn"
              className="flex items-center gap-2 px-4 py-3 bg-stone-100 text-[#292524] rounded-full font-medium hover:bg-stone-200 transition-all active:scale-95"
            >
              <Bell className="w-5 h-5" />
              <span className="hidden sm:inline">{lang === 'hr' ? 'Podsjetnik' : 'Reminder'}</span>
            </button>
            <Link
              to="/bereket/stats"
              data-testid="bereket-stats-link"
              className="flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-full font-medium hover:bg-accent/90 transition-all active:scale-95 shadow-md"
            >
              <TrendingUp className="w-5 h-5" />
              {lang === 'hr' ? 'Statistika' : 'Stats'}
            </Link>
          </div>
        </div>

        {/* Quick Access: Zikr, Dnevnik, Planer */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <button
            onClick={() => navigate('/dhikr')}
            data-testid="quick-zikr"
            className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95"
          >
            <SparklesIcon className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-purple-700">{lang === 'hr' ? 'Zikr' : 'Dhikr'}</p>
            <p className="text-xs text-purple-400 mt-0.5">{lang === 'hr' ? 'Tesbihat & dova' : 'Tasbeeh & dua'}</p>
          </button>
          <button
            onClick={() => navigate('/journal')}
            data-testid="quick-journal"
            className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95"
          >
            <PenLine className="w-6 h-6 text-rose-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-rose-700">{lang === 'hr' ? 'Dnevnik' : 'Journal'}</p>
            <p className="text-xs text-rose-400 mt-0.5">{lang === 'hr' ? 'Refleksija & šukr' : 'Reflection & shukr'}</p>
          </button>
          <button
            onClick={() => navigate('/planner')}
            data-testid="quick-planner"
            className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95"
          >
            <Calendar className="w-6 h-6 text-sky-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-sky-700">{lang === 'hr' ? 'Planer' : 'Planner'}</p>
            <p className="text-xs text-sky-400 mt-0.5">{lang === 'hr' ? 'Dnevni plan' : 'Daily plan'}</p>
          </button>
        </div>

        {/* ===== DAILY PLAN SECTION ===== */}
        <div className="bg-white/90 backdrop-blur-sm border border-stone-200/60 rounded-2xl overflow-hidden mb-6 shadow-sm" data-testid="daily-plan-section">
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 px-5 py-4 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4.5 h-4.5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-[#292524] text-base">
                    {lang === 'hr' ? 'Moj Dnevni Plan' : 'My Daily Plan'}
                  </h2>
                  <p className="text-xs text-[#78716C]">
                    {lang === 'hr' ? 'Poredaj i planiraj aktivnosti za danas' : 'Order and plan activities for today'}
                  </p>
                </div>
              </div>
              {dailyPlan.items.length > 0 && (
                <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                  {dailyPlan.items.filter(i => isActivityLogged(i.activity_id)).length}/{dailyPlan.items.length}
                </span>
              )}
            </div>
          </div>
          
          {dailyPlan.items.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-[#A8A29E] mb-1">
                {lang === 'hr' 
                  ? 'Klikni + na aktivnostima ispod da dodaš u dnevni plan' 
                  : 'Click + on activities below to add to your daily plan'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {dailyPlan.items.map((item, index) => {
                const logged = isActivityLogged(item.activity_id);
                return (
                  <div
                    key={item.activity_id}
                    data-testid={`plan-item-${item.activity_id}`}
                    className={`flex items-center gap-3 px-4 py-3 transition-all duration-300 ${logged ? 'bg-green-50/60' : 'hover:bg-stone-50/50'}`}
                  >
                    {/* Complete checkbox */}
                    <button
                      onClick={async () => {
                        if (!logged) {
                          // Find the full activity to get correct id
                          const fullActivity = [...activities, ...customActivities].find(a => a.activity_id === item.activity_id);
                          const isStandard = activities.some(a => a.activity_id === item.activity_id);
                          
                          if (isStandard && fullActivity) {
                            // Standard bereket activity - log to bereket_logs
                            logActivity(fullActivity);
                          } else {
                            // Custom activity - can't log to bereket_logs (FK constraint)
                            // Save completion locally
                            const newCompletions = {
                              date: getTodayDateKey(),
                              completed: [...planCompletions.completed, item.activity_id]
                            };
                            setPlanCompletions(newCompletions);
                            localStorage.setItem('bereket_plan_completions', JSON.stringify(newCompletions));
                            toast.success(lang === 'hr' ? `+${item.base_points} bodova!` : `+${item.base_points} points!`);
                          }
                        }
                      }}
                      disabled={logged || loading}
                      data-testid={`plan-complete-${item.activity_id}`}
                      className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-300 ${
                        logged
                          ? 'bg-green-500 text-white scale-110'
                          : 'border-2 border-stone-300 hover:border-primary hover:bg-primary/5 active:scale-95'
                      }`}
                    >
                      {logged && <Check className="w-4 h-4" />}
                    </button>

                    {/* Activity info */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm transition-all duration-300 ${logged ? 'text-green-700 line-through' : 'text-[#292524]'}`}>
                        {lang === 'hr' ? item.name_hr : item.name_en}
                      </p>
                      <p className="text-xs text-[#A8A29E]">
                        {logged
                          ? <span className="text-green-600 font-semibold">+{item.base_points} {lang === 'hr' ? 'bodova' : 'pts'}</span>
                          : <span>{item.base_points} {lang === 'hr' ? 'bodova' : 'pts'}</span>
                        }
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!logged && (
                        <>
                          <button onClick={() => movePlanItem(index, -1)} data-testid={`plan-move-up-${item.activity_id}`} className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30" disabled={index === 0}>
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => movePlanItem(index, 1)} data-testid={`plan-move-down-${item.activity_id}`} className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30" disabled={index === dailyPlan.items.length - 1}>
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setShowPlannerModal(item)}
                            data-testid={`add-to-planner-${item.activity_id}`}
                            className="p-1.5 text-primary hover:bg-primary/10 rounded-md transition-colors"
                            title={lang === 'hr' ? 'Dodaj u planer' : 'Add to planner'}
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => removeFromDailyPlan(item.activity_id)}
                        data-testid={`plan-remove-${item.activity_id}`}
                        className="p-1 text-stone-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add to Planner Modal */}
        {showPlannerModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="planner-modal">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
              <div className="bg-primary/5 px-5 py-4 border-b border-stone-100">
                <h3 className="font-heading font-semibold text-[#292524]">
                  {lang === 'hr' ? 'Dodaj u Planer' : 'Add to Planner'}
                </h3>
                <p className="text-xs text-[#78716C] mt-0.5">
                  {lang === 'hr' ? showPlannerModal.name_hr : showPlannerModal.name_en}
                </p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-[#57534E] mb-1.5 block">
                    {lang === 'hr' ? 'Datum' : 'Date'}
                  </label>
                  <input
                    type="date"
                    value={plannerDate}
                    onChange={(e) => setPlannerDate(e.target.value)}
                    data-testid="planner-modal-date"
                    className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-primary bg-stone-50"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#57534E] mb-1.5 block">
                    {lang === 'hr' ? 'Vrijeme' : 'Time'}
                  </label>
                  <input
                    type="time"
                    value={plannerTime}
                    onChange={(e) => setPlannerTime(e.target.value)}
                    data-testid="planner-modal-time"
                    className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-primary bg-stone-50"
                  />
                </div>
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-stone-50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={plannerReminder}
                    onChange={(e) => setPlannerReminder(e.target.checked)}
                    data-testid="planner-modal-reminder"
                    className="w-4 h-4 rounded border-stone-300 text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-[#292524]">
                      {lang === 'hr' ? 'Podsjetnik' : 'Reminder'}
                    </span>
                    <p className="text-xs text-[#78716C]">
                      {lang === 'hr' ? 'Dobij notifikaciju u zakazano vrijeme' : 'Get notified at scheduled time'}
                    </p>
                  </div>
                </label>
              </div>
              <div className="flex gap-2 px-5 pb-5">
                <button
                  onClick={() => setShowPlannerModal(null)}
                data-testid="planner-modal-cancel"
                  className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm font-medium text-[#57534E] hover:bg-stone-50"
                >
                  {lang === 'hr' ? 'Otkaži' : 'Cancel'}
                </button>
                <button
                  onClick={addActivityToPlanner}
                  data-testid="planner-modal-save"
                  className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  {lang === 'hr' ? 'Spremi' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== CUSTOM ACTIVITIES SECTION (moved here - right after Daily Plan) ===== */}
        <div className="bg-white/90 backdrop-blur-sm border border-stone-200/60 rounded-2xl overflow-hidden mb-6 shadow-sm" data-testid="custom-activities-section">
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 px-5 py-4 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Star className="w-4.5 h-4.5 text-yellow-600" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-[#292524] text-base">
                    {lang === 'hr' ? 'Moje Aktivnosti' : 'My Activities'}
                  </h2>
                  <p className="text-xs text-[#78716C]">
                    {lang === 'hr' ? 'Kreiraj i prati vlastite aktivnosti' : 'Create and track your own activities'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                data-testid="add-custom-activity-btn"
                className="flex items-center gap-1.5 px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-medium text-xs hover:bg-yellow-200 transition-all"
              >
                <Plus className="w-3.5 h-3.5" />
                {lang === 'hr' ? 'Nova' : 'New'}
              </button>
            </div>
          </div>

          {customActivities.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-[#A8A29E]">
                {lang === 'hr' ? 'Dodaj vlastite aktivnosti za praćenje bereketa' : 'Add your own activities to track blessings'}
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                data-testid="create-first-custom-activity-button"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-xl text-sm font-medium hover:bg-yellow-100 transition-all border border-yellow-200"
              >
                <Plus className="w-4 h-4" />
                {lang === 'hr' ? 'Kreiraj prvu aktivnost' : 'Create first activity'}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {customActivities.map((activity) => {
                const logged = isActivityLogged(activity.activity_id);
                return (
                  <div
                    key={activity.activity_id}
                    data-testid={`custom-activity-${activity.activity_id}`}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${logged ? 'bg-yellow-50/50' : 'hover:bg-stone-50/50'}`}
                  >
                    {/* Icon */}
                    <span className="text-xl flex-shrink-0">
                      {ACTIVITY_ICONS.find(i => i.id === activity.icon)?.icon || '⭐'}
                    </span>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm ${logged ? 'text-yellow-700' : 'text-[#292524]'}`}>
                        {lang === 'hr' ? activity.name_hr : activity.name_en}
                      </p>
                      {(activity.description_hr || activity.description_en) && (
                        <p className="text-xs text-[#A8A29E] truncate">
                          {lang === 'hr' ? activity.description_hr : activity.description_en}
                        </p>
                      )}
                      <span className="text-[10px] text-[#A8A29E]">+{activity.base_points} {lang === 'hr' ? 'bodova' : 'pts'}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Add to Plan */}
                      <button
                        onClick={() => addToDailyPlan(activity)}
                        data-testid={`plan-add-${activity.activity_id}`}
                        className="relative text-[10px] px-2 py-1 bg-stone-100 hover:bg-yellow-100 text-stone-500 hover:text-yellow-700 rounded-md transition-colors overflow-visible"
                        title={lang === 'hr' ? 'Dodaj u plan' : 'Add to plan'}
                      >
                        {addedAnimation === activity.activity_id && (
                          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="absolute w-8 h-8 bg-yellow-200 rounded-full animate-ping" />
                            <Check className="w-4 h-4 text-yellow-600 animate-bounce" />
                          </span>
                        )}
                        <span className={addedAnimation === activity.activity_id ? 'opacity-0' : ''}>
                          + {lang === 'hr' ? 'Plan' : 'Plan'}
                        </span>
                      </button>
                      {/* Add to Planner */}
                      <button
                        onClick={() => setShowPlannerModal(activity)}
                        data-testid={`add-to-planner-${activity.activity_id}`}
                        className="text-[10px] px-1.5 py-1 bg-stone-100 hover:bg-yellow-100 text-stone-500 hover:text-yellow-700 rounded-md transition-colors"
                        title={lang === 'hr' ? 'Dodaj u planer' : 'Add to planner'}
                      >
                        <Calendar className="w-3 h-3" />
                      </button>
                      {/* Log activity */}
                      {logged ? (
                        <div className="flex items-center gap-0.5 text-yellow-600 pl-1">
                          <Check className="w-4 h-4" />
                          <span className="text-xs font-bold">{getActivityPoints(activity.activity_id)}</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => logActivity(activity)}
                          disabled={loading}
                          data-testid={`log-custom-activity-${activity.activity_id}`}
                          className="p-1.5 rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-600 transition-all active:scale-95"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      )}
                      {/* Delete */}
                      <button
                        onClick={() => deleteCustomActivity(activity.activity_id)}
                        data-testid={`delete-custom-activity-${activity.activity_id}`}
                        className="p-1 text-stone-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#57534E] mb-1">
                {lang === 'hr' ? 'Današnji Bodovi' : "Today's Points"}
              </p>
              <p
                className="text-5xl font-heading font-bold text-primary"
                data-testid="today-points-display"
              >
                {todayData?.today_points || 0}
              </p>
            </div>
            <div className="text-right">
              <div
                className="text-5xl mb-2"
                data-testid="today-level-emoji"
              >
                {todayData?.today_level_emoji || '🌾'}
              </div>
              <p className="text-sm font-medium text-[#292524]">
                {lang === 'hr'
                  ? todayData?.today_level
                      ?.replace('Day of Spiritual Breakthrough', 'Dan duhovnog proboja')
                      .replace('Day of Barakah', 'Dan bereketa')
                      .replace('Day of Growth', 'Dan rasta')
                      .replace('Day of Awareness', 'Dan svjesnosti')
                      .replace('Starting the Journey', 'Početak puta')
                  : todayData?.today_level || 'Starting the Journey'}
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-primary/20">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#57534E]">
                {lang === 'hr' ? 'Napredak prema sljedećoj razini' : 'Progress to Next Level'}
              </span>
              <span className="font-medium text-[#292524]">
                {todayData?.today_points >= 36
                  ? '✨ Max'
                  : todayData?.today_points >= 21
                  ? `${36 - todayData?.today_points} ${lang === 'hr' ? 'do proboja' : 'to breakthrough'}`
                  : todayData?.today_points >= 11
                  ? `${21 - todayData?.today_points} ${lang === 'hr' ? 'do bereketa' : 'to barakah'}`
                  : todayData?.today_points >= 5
                  ? `${11 - todayData?.today_points} ${lang === 'hr' ? 'do rasta' : 'to growth'}`
                  : `${5 - (todayData?.today_points || 0)} ${lang === 'hr' ? 'do svjesnosti' : 'to awareness'}`}
              </span>
            </div>
          </div>
        </div>

        {/* 21 Načina za Postizanje Bereketa */}
        <div className="mt-8 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowBereketWays(!showBereketWays)}
            data-testid="bereket-ways-toggle"
            className="w-full p-6 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-amber-600" />
              </div>
              <div className="text-left">
                <h2 className="text-xl font-heading font-semibold text-[#292524]">
                  {lang === 'hr' ? '21 Način za Postizanje Bereketa' : '21 Ways to Achieve Barakah'}
                </h2>
                <p className="text-sm text-amber-700">
                  {lang === 'hr' 
                    ? 'Jednostavni koraci za privlačenje Allahovog blagoslova' 
                    : 'Simple steps to attract Allah\'s blessings'}
                </p>
              </div>
            </div>
            {showBereketWays ? (
              <ChevronUp className="w-6 h-6 text-amber-600" />
            ) : (
              <ChevronDown className="w-6 h-6 text-amber-600" />
            )}
          </button>
          
          {showBereketWays && (
            <div className="px-6 pb-6 space-y-3">
              {BEREKET_WAYS.map((way) => (
                <div
                  key={way.number}
                  className="bg-white rounded-xl border border-amber-100 overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setExpandedWay(expandedWay === way.number ? null : way.number)}
                        data-testid={`bereket-way-toggle-${way.number}`}
                    className="w-full p-4 flex items-center gap-4 hover:bg-amber-50/50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
                      {way.icon}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                          #{way.number}
                        </span>
                        <h3 className="font-semibold text-[#292524]">
                          {lang === 'hr' ? way.title_hr : way.title_en}
                        </h3>
                      </div>
                    </div>
                    {expandedWay === way.number ? (
                      <ChevronUp className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    )}
                  </button>
                  
                  {expandedWay === way.number && (
                    <div className="px-4 pb-4 pt-0">
                      <div className="pl-14">
                        <p className="text-sm text-[#57534E] leading-relaxed">
                          {lang === 'hr' ? way.description_hr : way.description_en}
                        </p>
                        <div className="mt-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            way.category === 'prayer' ? 'bg-green-100 text-green-700' :
                            way.category === 'dhikr' ? 'bg-purple-100 text-purple-700' :
                            way.category === 'quran' ? 'bg-blue-100 text-blue-700' :
                            way.category === 'charity' ? 'bg-pink-100 text-pink-700' :
                            way.category === 'sunnah' ? 'bg-amber-100 text-amber-700' :
                            way.category === 'family' ? 'bg-rose-100 text-rose-700' :
                            'bg-stone-100 text-stone-700'
                          }`}>
                            {lang === 'hr' 
                              ? (way.category === 'prayer' ? 'Namaz' :
                                 way.category === 'dhikr' ? 'Zikr' :
                                 way.category === 'quran' ? 'Kur\'an' :
                                 way.category === 'charity' ? 'Sadaka' :
                                 way.category === 'sunnah' ? 'Sunnet' :
                                 way.category === 'family' ? 'Obitelj' : 'Osobno')
                              : (way.category === 'prayer' ? 'Prayer' :
                                 way.category === 'dhikr' ? 'Dhikr' :
                                 way.category === 'quran' ? 'Quran' :
                                 way.category === 'charity' ? 'Charity' :
                                 way.category === 'sunnah' ? 'Sunnah' :
                                 way.category === 'family' ? 'Family' : 'Personal')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-8 mt-8">
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-xl">🟢</span>
              </div>
              <div>
                <h2 className="text-xl font-heading font-semibold text-[#292524]">
                  {lang === 'hr' ? 'Osnovni Bereket' : 'Basic Barakah'}
                </h2>
                <p className="text-sm text-[#57534E]">
                  1-3 {lang === 'hr' ? 'boda po aktivnosti' : 'points per activity'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {basicActivities.map((activity) => (
                <div
                  key={activity.activity_id}
                  className={`bg-white border-2 rounded-xl p-5 transition-all hover:shadow-md ${
                    isActivityLogged(activity.activity_id)
                      ? 'border-primary bg-primary/5'
                      : 'border-stone-200'
                  }`}
                  data-testid={`activity-card-${activity.activity_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading font-semibold text-[#292524] flex-1">
                      {lang === 'hr' ? activity.name_hr : activity.name_en}
                    </h3>
                    {isActivityLogged(activity.activity_id) ? (
                      <div className="flex items-center gap-1 text-primary">
                        <Check className="w-5 h-5" />
                        <span className="font-bold">
                          {getActivityPoints(activity.activity_id)}
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={() => logActivity(activity)}
                        disabled={loading}
                        data-testid={`log-activity-${activity.activity_id}`}
                        className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-all active:scale-95"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-[#57534E] mb-3">
                    {lang === 'hr'
                      ? activity.description_hr
                      : activity.description_en}
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#A8A29E]">
                      {lang === 'hr' ? 'Do' : 'Up to'} {activity.max_daily_points}{' '}
                      {lang === 'hr' ? 'bodova/dan' : 'pts/day'}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => addToDailyPlan(activity)}
                        data-testid={`plan-add-${activity.activity_id}`}
                        className="relative text-[10px] px-2 py-1 bg-stone-100 hover:bg-primary/10 text-stone-500 hover:text-primary rounded-md transition-colors overflow-visible"
                        title={lang === 'hr' ? 'Dodaj u plan' : 'Add to plan'}
                      >
                        {addedAnimation === activity.activity_id && (
                          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="absolute w-8 h-8 bg-primary/20 rounded-full animate-ping" />
                            <Check className="w-4 h-4 text-primary animate-bounce" />
                          </span>
                        )}
                        <span className={addedAnimation === activity.activity_id ? 'opacity-0' : ''}>
                          + {lang === 'hr' ? 'Plan' : 'Plan'}
                        </span>
                      </button>
                      <button
                        onClick={() => setShowPlannerModal(activity)}
                        data-testid={`quick-add-to-planner-${activity.activity_id}`}
                        className="text-[10px] px-2 py-1 bg-stone-100 hover:bg-primary/10 text-stone-500 hover:text-primary rounded-md transition-colors"
                        title={lang === 'hr' ? 'Dodaj u planer' : 'Add to planner'}
                      >
                        <Calendar className="w-3 h-3 inline" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xl">🔵</span>
              </div>
              <div>
                <h2 className="text-xl font-heading font-semibold text-[#292524]">
                  {lang === 'hr' ? 'Srednji Bereket' : 'Medium Barakah'}
                </h2>
                <p className="text-sm text-[#57534E]">
                  3 {lang === 'hr' ? 'boda po aktivnosti' : 'points per activity'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mediumActivities.map((activity) => (
                <div
                  key={activity.activity_id}
                  className={`bg-white border-2 rounded-xl p-5 transition-all hover:shadow-md ${
                    isActivityLogged(activity.activity_id)
                      ? 'border-accent bg-accent/5'
                      : 'border-stone-200'
                  }`}
                  data-testid={`activity-card-${activity.activity_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading font-semibold text-[#292524] flex-1">
                      {lang === 'hr' ? activity.name_hr : activity.name_en}
                    </h3>
                    {isActivityLogged(activity.activity_id) ? (
                      <div className="flex items-center gap-1 text-accent">
                        <Check className="w-5 h-5" />
                        <span className="font-bold">3</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => logActivity(activity)}
                        disabled={loading}
                        data-testid={`log-activity-${activity.activity_id}`}
                        className="p-2 rounded-full bg-accent/10 hover:bg-accent/20 text-accent transition-all active:scale-95"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-[#57534E]">
                    {lang === 'hr'
                      ? activity.description_hr
                      : activity.description_en}
                  </p>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <button onClick={() => addToDailyPlan(activity)} data-testid={`medium-plan-add-${activity.activity_id}`} className="relative text-[10px] px-2 py-1 bg-stone-100 hover:bg-accent/10 text-stone-500 hover:text-accent rounded-md transition-colors overflow-visible">
                      {addedAnimation === activity.activity_id && (
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="absolute w-8 h-8 bg-accent/20 rounded-full animate-ping" />
                          <Check className="w-4 h-4 text-accent animate-bounce" />
                        </span>
                      )}
                      <span className={addedAnimation === activity.activity_id ? 'opacity-0' : ''}>
                        + {lang === 'hr' ? 'Plan' : 'Plan'}
                      </span>
                    </button>
                    <button onClick={() => setShowPlannerModal(activity)} data-testid={`medium-add-to-planner-${activity.activity_id}`} className="text-[10px] px-2 py-1 bg-stone-100 hover:bg-accent/10 text-stone-500 hover:text-accent rounded-md transition-colors">
                      <Calendar className="w-3 h-3 inline" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-xl">🟣</span>
              </div>
              <div>
                <h2 className="text-xl font-heading font-semibold text-[#292524]">
                  {lang === 'hr' ? 'Veliki Bereket' : 'Advanced Barakah'}
                </h2>
                <p className="text-sm text-[#57534E]">
                  5 {lang === 'hr' ? 'bodova po aktivnosti' : 'points per activity'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {advancedActivities.map((activity) => (
                <div
                  key={activity.activity_id}
                  className={`bg-white border-2 rounded-xl p-5 transition-all hover:shadow-md ${
                    isActivityLogged(activity.activity_id)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-stone-200'
                  }`}
                  data-testid={`activity-card-${activity.activity_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading font-semibold text-lg text-[#292524] flex-1">
                      {lang === 'hr' ? activity.name_hr : activity.name_en}
                    </h3>
                    {isActivityLogged(activity.activity_id) ? (
                      <div className="flex items-center gap-1 text-purple-600">
                        <Award className="w-6 h-6" />
                        <span className="font-bold text-lg">5</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => logActivity(activity)}
                        disabled={loading}
                        data-testid={`log-activity-${activity.activity_id}`}
                        className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 transition-all active:scale-95"
                      >
                        <Plus className="w-6 h-6" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-[#57534E]">
                    {lang === 'hr'
                      ? activity.description_hr
                      : activity.description_en}
                  </p>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <button onClick={() => addToDailyPlan(activity)} data-testid={`advanced-plan-add-${activity.activity_id}`} className="relative text-[10px] px-2 py-1 bg-stone-100 hover:bg-purple-100 text-stone-500 hover:text-purple-600 rounded-md transition-colors overflow-visible">
                      {addedAnimation === activity.activity_id && (
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="absolute w-8 h-8 bg-purple-200 rounded-full animate-ping" />
                          <Check className="w-4 h-4 text-purple-600 animate-bounce" />
                        </span>
                      )}
                      <span className={addedAnimation === activity.activity_id ? 'opacity-0' : ''}>
                        + {lang === 'hr' ? 'Plan' : 'Plan'}
                      </span>
                    </button>
                    <button onClick={() => setShowPlannerModal(activity)} data-testid={`advanced-add-to-planner-${activity.activity_id}`} className="text-[10px] px-2 py-1 bg-stone-100 hover:bg-purple-100 text-stone-500 hover:text-purple-600 rounded-md transition-colors">
                      <Calendar className="w-3 h-3 inline" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-8 bg-accent/10 border border-accent/20 rounded-2xl p-6">
          <h3 className="font-heading font-semibold text-lg text-[#292524] mb-3">
            {lang === 'hr' ? '💎 Bonus Bodovi (+2)' : '💎 Bonus Points (+2)'}
          </h3>
          <ul className="space-y-2 text-sm text-[#57534E]">
            <li className="flex items-start gap-2">
              <span className="text-accent mt-1">•</span>
              <span>
                {lang === 'hr'
                  ? 'Učini nešto u tajnosti (neka nitko ne zna)'
                  : 'Do something in secret (let no one know)'}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent mt-1">•</span>
              <span>
                {lang === 'hr'
                  ? 'Oprosti nekome od srca'
                  : 'Forgive someone from the heart'}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent mt-1">•</span>
              <span>
                {lang === 'hr' ? 'Savladaj svoj ego' : 'Overcome your ego'}
              </span>
            </li>
          </ul>
        </div>

      </div>

      {/* Add Custom Activity Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div 
            className="bg-white rounded-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-bold text-[#292524]">
                {lang === 'hr' ? 'Nova Aktivnost' : 'New Activity'}
              </h2>
              <button onClick={() => setShowAddModal(false)} data-testid="custom-activity-modal-close-button" className="p-2 hover:bg-stone-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Ikona' : 'Icon'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {ACTIVITY_ICONS.map(icon => (
                    <button
                      key={icon.id}
                      onClick={() => setNewActivity(prev => ({ ...prev, icon: icon.id }))}
                      data-testid={`custom-activity-icon-${icon.id}`}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all ${
                        newActivity.icon === icon.id 
                          ? 'bg-yellow-100 ring-2 ring-yellow-500' 
                          : 'bg-stone-100 hover:bg-stone-200'
                      }`}
                    >
                      {icon.icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name HR */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Naziv (HR)' : 'Name (HR)'}
                </label>
                <input
                  type="text"
                  value={newActivity.name_hr}
                  onChange={(e) => setNewActivity(prev => ({ ...prev, name_hr: e.target.value }))}
                  data-testid="custom-activity-name-hr-input"
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                  placeholder="npr. Posjeta rodbine"
                />
              </div>

              {/* Name EN */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Naziv (EN)' : 'Name (EN)'}
                </label>
                <input
                  type="text"
                  value={newActivity.name_en}
                  onChange={(e) => setNewActivity(prev => ({ ...prev, name_en: e.target.value }))}
                  data-testid="custom-activity-name-en-input"
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                  placeholder="e.g. Visit relatives"
                />
              </div>

              {/* Points */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Bodovi' : 'Points'}
                </label>
                <select
                  value={newActivity.base_points}
                  onChange={(e) => setNewActivity(prev => ({ ...prev, base_points: parseInt(e.target.value) }))}
                  data-testid="custom-activity-points-select"
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} {lang === 'hr' ? 'bodova' : 'points'}</option>
                  ))}
                </select>
              </div>

              {/* Description (optional) */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Opis (opcionalno)' : 'Description (optional)'}
                </label>
                <textarea
                  value={newActivity.description_hr}
                  onChange={(e) => setNewActivity(prev => ({ ...prev, description_hr: e.target.value, description_en: e.target.value }))}
                  data-testid="custom-activity-description-textarea"
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg resize-none h-20 text-[#292524]"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                data-testid="custom-activity-cancel-button"
                className="flex-1 py-3 bg-stone-100 text-[#57534E] rounded-lg font-medium"
              >
                {lang === 'hr' ? 'Odustani' : 'Cancel'}
              </button>
              <button
                onClick={addCustomActivityHandler}
                data-testid="custom-activity-submit-button"
                className="flex-1 py-3 bg-primary text-white rounded-lg font-medium"
              >
                {lang === 'hr' ? 'Dodaj' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Settings Modal */}
      <ReminderSettings 
        isOpen={showReminderModal} 
        onClose={() => setShowReminderModal(false)} 
      />
    </Layout>
  );
}